import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { DeviceService } from '../src/DeviceService'
import { PinResult, StaffSession, lockDevice, signInWithPin } from './services/StaffAuthService';

/**
 * Two sessions, not one.
 *
 * `isAuthenticated` is the **property** session: a manager signs the device in
 * once with an email and password and it stays that way. `staff` is whoever is
 * currently holding the device, established with a PIN and cleared many times a
 * service. Guarded screens need both; the PIN screen needs only the first.
 *
 * Keeping them apart is the whole point. `lock()` ends the staff session so the
 * next waiter is two seconds from working, while `logout()` ends the property
 * session and means fetching a manager.
 */
interface AuthContextType {
  isAuthenticated: boolean;
  authChecked: boolean;
  isMfaCompleted: boolean;
  /** Who is signed in by PIN, or null when the device is locked. */
  staff: StaffSession | null;
  /**
   * Whether the signed-in staff member is the person who holds the venue's own
   * credentials. Only they are offered the destructive sign-out inside the app.
   */
  isAccountOwner: boolean;
  permissions: string[];
  permissionsLoaded: boolean;
  hasPerm: (key: string) => boolean;
  hasAnyPerm: (keys: string[]) => boolean;
  refreshPermissions: () => Promise<void>;
  login: () => void;
  /** Sign a staff member in by PIN, inside the existing property session. */
  pinLogin: (pin: string) => Promise<PinResult>;
  /** End the staff session only, returning the device to the PIN screen. */
  lock: () => Promise<void>;
  completeMfa: () => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

const fetchPermissionsFromApi = async (): Promise<string[]> => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/Authentication/get-permissions`, {
      method: 'GET',
      credentials: 'include',
    });
    if (!response.ok) return [];
    const data = await response.json();
    // data is either a JSON string or already parsed array
    if (Array.isArray(data)) return data;
    if (typeof data === 'string') {
      try {
        const parsed = JSON.parse(data);
        if (Array.isArray(parsed)) return parsed;
      } catch { }
    }
    return [];
  } catch {
    return [];
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [authChecked, setAuthChecked] = useState<boolean>(false);
  const [isMfaCompleted, setIsMfaCompleted] = useState<boolean>(false);
  const [staff, setStaff] = useState<StaffSession | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [permissionsLoaded, setPermissionsLoaded] = useState<boolean>(false);

  const loadPermissions = useCallback(async () => {
    const perms = await fetchPermissionsFromApi();
    setPermissions(perms);
    setPermissionsLoaded(true);
  }, []);

  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/authentication/status`, {
          method: 'GET',
          credentials: 'include',
        });

        if (response.ok) {
          const data = await response.json();
          setIsAuthenticated(true);
          setIsMfaCompleted(data.isMfaCompleted);
          // A page refresh must not throw the waiter back to the PIN screen, so
          // the staff session is restored from the server alongside the property
          // one. Older responses simply omit it, leaving the device locked.
          setStaff(data.staff ?? null);
          if (data.staff) await loadPermissions();
        } else {
          setIsAuthenticated(false);
          setIsMfaCompleted(false);
          setStaff(null);
        }
      } catch (error) {
        console.error('Failed to check authentication status:', error);
        setIsAuthenticated(false);
        setIsMfaCompleted(false);
        setStaff(null);
      } finally {
        setAuthChecked(true);
      }
    };

    checkAuthStatus();
  }, [loadPermissions]);

  const login = () => {
    setIsAuthenticated(true);
  };

  const pinLogin = async (pin: string): Promise<PinResult> => {
    const result = await signInWithPin(pin);
    if (result.ok) {
      setIsAuthenticated(true);
      setStaff(result.staff);
      // Permissions belong to the person, so they have to be refetched here
      // rather than carried over from whoever held the device last.
      await loadPermissions();
      try {
        await DeviceService.registerDevice();
      } catch (err) {
        console.error('Device registration failed:', err);
      }
    }
    return result;
  };

  const lock = async () => {
    await lockDevice();
    setStaff(null);
    setPermissions([]);
    setPermissionsLoaded(false);
  };

  const completeMfa = async () => {
    setIsMfaCompleted(true);
    await loadPermissions();
  };

  const logout = async () => {
    try {
      try {
        await DeviceService.unregisterDevice();
        console.log("Device unregistered before logout");
      } catch (err) {
        console.error("Device unregister failed:", err);
      }
      await fetch(`${process.env.REACT_APP_API_URL}/authentication/logout`, {
        method: 'POST',
        credentials: 'include',
      });
      setIsAuthenticated(false);
      setIsMfaCompleted(false);
      setStaff(null);
      setPermissions([]);
      setPermissionsLoaded(false);
      localStorage.removeItem('deviceId');
      localStorage.removeItem('propertyId');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  /**
   * The owner is whoever signs in by PIN under the email the device was logged in
   * with. There is no owner flag on the staff record to read, and inventing a role
   * would be worse: this is exactly the person who can already end the property
   * session, so it grants nothing new.
   */
  const accountEmail = (localStorage.getItem('propertyAccountEmail') || '').toLowerCase();
  const isAccountOwner = !!staff && !!accountEmail
    && (staff.userName || '').toLowerCase() === accountEmail;

  // While permissions haven't loaded yet, grant access to avoid flash of "Access Denied"
  const hasPerm = (key: string) => !permissionsLoaded ? true : permissions.includes(key);
  const hasAnyPerm = (keys: string[]) => !permissionsLoaded ? true : keys.some(k => permissions.includes(k));

  return (
    <AuthContext.Provider value={{
      isAuthenticated, authChecked, isMfaCompleted, staff, isAccountOwner,
      permissions, permissionsLoaded, hasPerm, hasAnyPerm,
      refreshPermissions: loadPermissions,
      login, pinLogin, lock, completeMfa, logout
    }}>
      {children}
    </AuthContext.Provider>
  );
};
